import React, { useEffect, useState } from "react";
import client, { formatZAR } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus } from "lucide-react";
import ItemFormDialog from "@/components/ItemFormDialog";
import ImageDropZone from "./ImageDropZone";
import { ModuleHeader, EmptyState } from "./AdminUI";
import { SPECIAL_SECTIONS, errMsg } from "./adminShared";

function SpecialRow({ s, onSave, onEdit, onDelete }) {
    return (
        <div data-testid={`special-row-${s.slug}`} className="flex flex-wrap items-center gap-4 px-4 py-3 border-t border-brand-line text-sm">
            <ImageDropZone currentUrl={s.image_url} onUploaded={(url) => onSave(s.id, { image_url: url })} />
            <div className="flex-1 min-w-[180px]">
                <p className="text-brand-cream">{s.name}</p>
                <p className="text-xs text-brand-mute">
                    {formatZAR(s.price)} · {s.available_date ? `Only on ${s.available_date}` : "Every day"}
                    {s.sold_out && <span className="text-red-300 ml-2 uppercase tracking-wider text-[10px]">Sold out today</span>}
                </p>
            </div>
            <label className="flex items-center gap-2 text-xs text-brand-mute">
                <Switch data-testid={`special-active-${s.slug}`} checked={!!s.active} onCheckedChange={(v) => onSave(s.id, { active: v })} /> Active
            </label>
            <label className="flex items-center gap-2 text-xs text-brand-mute">
                <Switch data-testid={`special-soldout-${s.slug}`} checked={!!s.sold_out} onCheckedChange={(v) => onSave(s.id, { sold_out_today: v })} /> Sold out
            </label>
            <div className="flex gap-1.5">
                <button data-testid={`special-edit-${s.slug}`} onClick={onEdit} className="p-1.5 border border-brand-line hover:border-brand-olive hover:text-brand-gold rounded-sm"><Pencil size={13} /></button>
                <button data-testid={`special-delete-${s.slug}`} onClick={onDelete} className="p-1.5 border border-brand-line hover:border-destructive hover:text-destructive rounded-sm"><Trash2 size={13} /></button>
            </div>
        </div>
    );
}

export default function SpecialsModule() {
    const [list, setList] = useState([]);
    const [sections, setSections] = useState([]);
    const [dialog, setDialog] = useState(null);

    const load = () => client.get("/admin/specials").then((r) => setList(r.data));
    useEffect(() => {
        load();
        client.get("/categories").then((r) => setSections(r.data.filter((c) => SPECIAL_SECTIONS.includes(c.slug))));
    }, []);

    const save = async (id, updates) => {
        try {
            await client.patch(`/admin/specials/${id}`, updates);
            toast.success("Saved");
            load();
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        }
    };

    const remove = async (s) => {
        if (!window.confirm(`Delete "${s.name}"?`)) return;
        await client.delete(`/admin/specials/${s.id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-specials-module">
            <ModuleHeader
                eyebrow="Rotation"
                title="Specials & Bundles"
                subtitle="Daily specials, combos and pre-cooked dinners shown on the Specials page and Menu. Set a date to show a special on one day only."
                actions={<button data-testid="special-add-btn" onClick={() => setDialog({ mode: "create" })} className="btn-primary text-sm inline-flex items-center gap-2"><Plus size={14} /> Add special</button>}
            />
            <div className="space-y-8">
                {sections.map((sec) => {
                    const rows = list.filter((s) => s.section === sec.slug);
                    return (
                        <section key={sec.slug} data-testid={`specials-section-${sec.slug}`}>
                            <h2 className="font-display text-xl mb-3">{sec.name} <span className="text-sm text-brand-mute">({rows.length})</span></h2>
                            {rows.length === 0 ? <EmptyState>Nothing in this section yet.</EmptyState> : (
                                <div className="border border-brand-line rounded-sm bg-brand-charcoal [&>*:first-child]:border-t-0">
                                    {rows.map((s) => (
                                        <SpecialRow key={s.id} s={s} onSave={save} onEdit={() => setDialog({ mode: "edit", item: s })} onDelete={() => remove(s)} />
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
            <ItemFormDialog
                isSpecial
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                mode={dialog?.mode || "create"}
                item={dialog?.item}
                categories={sections}
                onSaved={load}
            />
        </div>
    );
}
